"use client";

import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import {
  createEmployee,
  deleteEmployee,
  fetchEmployees,
  updateEmployee,
  updatePasswordEmployee,
  uploadEmployeeImage,
} from "@/store/modules/employees/thunks";
import { fetchProducts } from "@/store/modules/products/thunks";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { employeeCreateSchema, employeeSchema } from "@/validations";
import { passwordSchema } from "@/validations/employee";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useForm as usePasswordForm } from "react-hook-form";

const useEmployee = (
  onSuccess?: () => void,
  togglePasswordModal?: () => void,
) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const { hasPermission, loading: loadingPermission } = usePermission();
  const { establishment, baseURL } = useAuth();
  const [searchValue, setSearchValue] = useState<string>(params.search || "");
  const [employeeId, setemployeeId] = useState<string | number | null>(null);
  // Imagem selecionada antes da criação (upload só após create)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);

  // Cache inteligente - previne requisições duplicadas
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const lastParamsRef = useRef<string>("");

  const getSchema = () => {
    return employeeId ? employeeSchema : employeeCreateSchema;
  };

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(getSchema()),
    defaultValues: {
      name: "",
      email: "",
      role_id: "",
      show_in_schedule_and_site: true,
      can_view_all_schedules: false,
      password: "",
      password_confirmation: "",
      phone_whatsapp: "",
      cep: "",
      address: "",
      address_number: "",
      city: "",
      state: "",
      country: "",
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    control: controlPassword,
    formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword },
    reset: resetPasswordForm,
    watch: watchPasswordForm,
  } = usePasswordForm({
    resolver: yupResolver(passwordSchema),
  });

  const setFormData = (data: Partial<typeof employeeSchema.__outputType>) => {
    reset(data);
  };

  const onSubmit = async (data: typeof employeeSchema.__outputType) => {
    try {
      let result;

      if (employeeId && hasPermission("update user")) {
        // Normalizar booleans para evitar mismatches entre true/false e 1/0
        const payload: any = {
          id: employeeId,
          ...data,
          show_in_schedule_and_site: (data as any).show_in_schedule_and_site
            ? 1
            : 0,
          can_view_all_schedules: (data as any).can_view_all_schedules ? 1 : 0,
        };
        result = await dispatch(updateEmployee(payload));
      } else if (hasPermission("create user")) {
        // Se houver imagem pendente, enviar multipart/form-data
        if (pendingImageFile) {
          const formData = new FormData();
          Object.entries(data).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            // password_confirmation será tratado explicitamente mais abaixo
            if (key === "password_confirmation") return;
            let finalValue: any = value;
            if (typeof value === "boolean") {
              // Backend pode esperar 0/1 ou true/false; priorizamos 1/0
              finalValue = value ? "1" : "0";
            }
            formData.append(key, String(finalValue));
          });
          if (data.password) formData.append("password", data.password);
          // Sempre enviar password_confirmation quando existir (mesmo que vazia) para evitar mismatch por ausência
          formData.append(
            "password_confirmation",
            (data as any).password_confirmation || "",
          );
          formData.append("image", pendingImageFile);
          // Debug em dev: listar chaves
          if (process.env.NEXT_PUBLIC_IS_DEV) {
            // eslint-disable-next-line no-console
            console.log(
              "[EmployeeCreate] FormData keys:",
              Array.from(formData.keys()),
            );
          }
          result = await dispatch(createEmployee(formData as any));
        } else {
          result = await dispatch(
            createEmployee({
              ...data,
              // Normalizar boolean possivelmente esperado como 1/0
              show_in_schedule_and_site: data.show_in_schedule_and_site ? 1 : 0,
              can_view_all_schedules: data.can_view_all_schedules ? 1 : 0,
            }),
          );
        }
      } else {
        return false;
      }

      if (
        createEmployee.fulfilled.match(result) ||
        updateEmployee.fulfilled.match(result)
      ) {
        if (hasPermission("read user")) {
          dispatch(fetchEmployees(params));
        }
        if (hasPermission("read product")) {
          dispatch(fetchProducts(params));
        }
        if (!employeeId) {
          setPendingImageFile(null);
        }
        if (onSuccess) onSuccess();
        return true;
      } else {
        console.error("Erro na operação:", result);
        return false;
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
      return false;
    }
  };

  const updatePassword = async (
    id: string,
    password: string,
    password_confirmation: string,
  ) => {
    try {
      const result = await dispatch(
        updatePasswordEmployee({ id, password, password_confirmation }),
      );

      if (updatePasswordEmployee.fulfilled.match(result)) {
        if (onSuccess) onSuccess();
        return true;
      } else {
        console.error("Erro ao alterar senha");
        return false;
      }
    } catch (error) {
      console.error("Erro inesperado ao atualizar senha:", error);
      return false;
    }
  };

  const onSubmitPassword = async (data: any) => {
    if (!employeeId) return;
    const success = await updatePassword(
      String(employeeId),
      data.password,
      data.password_confirmation,
    );
    if (success) {
      resetPasswordForm();
      togglePasswordModal?.();
    }
  };

  const {
    employees,
    loading,
    error,
    fetched: employeesFetched,
  } = useSelector((state: any) => state.employees);
  const { products, fetched: productsFetched } = useSelector(
    (state: any) => state.products,
  );
  const dispatch: AppDispatch = useDispatch();

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Aguarda establishment e baseURL estarem prontos antes de fazer requests
    if (!baseURL || !establishment || hasFetchedRef.current) return;

    const payload = {
      ...params,
      // default per_page for employees listing: 25
      per_page:
        params.per_page && params.per_page !== 10 ? params.per_page : 25,
    };

    // Apply the per_page to params state so pagination reflects it
    if (params.per_page !== payload.per_page) {
      updateParams({ per_page: payload.per_page });
    }

    // Buscar dados se não foram buscados ainda
    if (!employeesFetched && hasPermission("read user")) {
      dispatch(fetchEmployees(payload));
    }

    if (!productsFetched && hasPermission("read product")) {
      dispatch(fetchProducts(payload));
    }

    hasFetchedRef.current = true;
  }, [
    params,
    employeesFetched,
    productsFetched,
    baseURL,
    establishment,
    hasPermission,
    dispatch,
  ]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const totalItems = employees?.total ?? 0;
  const itemsPerPage = employees?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;
  const pagesArray = Array.from({ length: totalPages }, (_, i) => i + 1);

  const [currentPage, setCurrentPage] = useState(employees?.current_page ?? 1);

  useEffect(() => {
    if (employees?.current_page && employees.current_page !== currentPage) {
      setCurrentPage(employees.current_page);
    }
  }, [employees?.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  const handleDelete = async () => {
    try {
      if (employeeId && hasPermission("delete user")) {
        await dispatch(deleteEmployee(Number(employeeId)));
        setemployeeId(null);

        return true;
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
    }
  };

  const handleUploadImage = async (
    employeeId: string | number,
    image: File,
  ) => {
    try {
      if (hasPermission("update user")) {
        const result = await dispatch(
          uploadEmployeeImage({ employeeId, image }),
        );

        if (uploadEmployeeImage.fulfilled.match(result)) {
          // Recarregar a lista de funcionários para refletir a mudança
          if (hasPermission("read user")) {
            dispatch(fetchEmployees(params));
          }
          return true;
        } else {
          console.error("Erro no upload da imagem:", result);
          return false;
        }
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      return false;
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    watch,
    setValue,
    employees,
    loading,
    error,
    employeeId,
    setemployeeId,
    handleDelete,
    setFormData,
    handlePage,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    pagesArray,
    control,
    updatePassword,
    onSubmitPassword,
    isSubmittingPassword,
    passwordErrors,
    controlPassword,
    registerPassword,
    handleSubmitPassword,
    resetPasswordForm,
    products,
    handleFilterChange,
    searchValue,
    handleUploadImage,
    setPendingImageFile,
    pendingImageFile,
  };
};

export default useEmployee;
